import { X } from 'lucide-react';
import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {{
 *   open: boolean;
 *   galleryTitle: string;
 *   phase: 'loading' | 'confirm' | 'downloading';
 *   downloadStep?: 'fetching' | 'packing';
 *   fileCount: number;
 *   totalBytes: number;
 *   error?: string | null;
 *   onClose: () => void;
 *   onConfirm: () => void;
 * }} props
 */
export function GalleryDownloadConfirmModal({
    open,
    galleryTitle,
    phase,
    downloadStep = 'fetching',
    fileCount,
    totalBytes,
    error = null,
    onClose,
    onConfirm,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const busy = phase === 'loading' || phase === 'downloading';
    const sizeMb =
        totalBytes > 0
            ? (totalBytes / (1024 * 1024)).toFixed(1)
            : null;

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !busy) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose, busy]);

    if (!open) return null;

    const canConfirm =
        phase === 'confirm' && fileCount > 0 && !error;

    return (
        <div
            className="app-modal-backdrop"
            onClick={busy ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="app-modal-header">
                    <h2 id={titleId} className="app-modal-title h6 mb-0">
                        {t('galleriesPage.downloadConfirmTitle')}
                    </h2>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={busy}
                        aria-label={t('galleriesPage.downloadConfirmCancel')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>
                <div className="app-modal-body">
                    <p className="gallery-download-modal-gallery-name small text-secondary mb-2">
                        {galleryTitle}
                    </p>
                    {phase === 'loading' ? (
                        <p className="mb-0" role="status" aria-live="polite">
                            {t('galleriesPage.downloadConfirmLoading')}
                        </p>
                    ) : (
                        <p className="mb-0">
                            {sizeMb
                                ? t('galleriesPage.downloadConfirmBody', {
                                      count: fileCount,
                                      sizeMb,
                                  })
                                : t(
                                      'galleriesPage.downloadConfirmBodyUnknownSize',
                                      { count: fileCount },
                                  )}
                        </p>
                    )}
                    {error ? (
                        <p
                            className="app-modal-error small text-danger mb-0 mt-3"
                            role="alert"
                        >
                            {error}
                        </p>
                    ) : null}
                </div>
                <div className="app-modal-footer">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={onClose}
                        disabled={busy}
                    >
                        {t('galleriesPage.downloadConfirmCancel')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onConfirm}
                        disabled={!canConfirm || phase === 'downloading'}
                    >
                        {phase === 'downloading'
                            ? downloadStep === 'packing'
                                ? t('galleriesPage.downloadPackingZip')
                                : t('galleriesPage.downloadInProgress')
                            : t('galleriesPage.downloadConfirmSubmit')}
                    </button>
                </div>
            </div>
        </div>
    );
}
